import React from "react"
import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material"
import {useParams} from "react-router-dom"

import {channelPresenter} from "./channelPresenter"
import {AssignButton} from "./components/AssignButton"
import {CandidateList} from "./components/CandidateCard"
import {DismissPanel} from "./components/DismissPanel"
import {IdentifyPanel} from "./components/IdentifyPanel"
import {ItemHeader} from "./components/ItemHeader"
import {OutcomeCard} from "./components/OutcomeCard"
import {SenderCheckbox} from "./components/SenderCheckbox"
import {Thread} from "./components/Thread"
import {useAssignCustomer, type AssignOutcome} from "./useAssignCustomer"
import {useUnidentifiedInbox} from "./useUnidentifiedInbox"

import {Layout} from "@indebted/components/Layout"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

const assignFailure: Record<Exclude<AssignOutcome, "assigned">, string> = {
	conflict: "A colleague assigned this message first.",
	forbidden: "You are not set up to assign messages in this inbox.",
	gone: "This message is no longer waiting to be identified.",
	rejected: "The assignment was refused.",
	failed: "Failed to assign the message.",
}

function PageFrame({children}: {children: React.ReactNode}) {
	return (
		<Layout>
			<Box px={3} pt={3} pb={4}>
				{children}
			</Box>
		</Layout>
	)
}

function ItemPage() {
	const {channel = "", conversationId = "", providerMessageId = ""} = useParams()
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const {data, loading, error, notFound, refetch} = useUnidentifiedInbox(
		channel,
		conversationId,
		providerMessageId,
		agentEmail,
	)
	const {assign, assigning} = useAssignCustomer(channel, conversationId, providerMessageId, agentEmail)
	const [addSenderEmail, setAddSenderEmail] = React.useState(false)

	const assignTo = async (customerID: string, name: string) => {
		const {outcome, reason} = await assign(customerID, addSenderEmail)
		if (outcome === "assigned") {
			notification.success(`Assigned to ${name}.`)
			refetch()
			return
		}

		notification.error(reason || assignFailure[outcome])
		// A refused assignment can mean the page is stale rather than wrong, so
		// re-read and let the agent see whatever the message actually says now.
		if (outcome === "conflict" || outcome === "gone") refetch()
	}

	if (loading) {
		return (
			<PageFrame>
				<Box display="flex" justifyContent="center" p={3}>
					<CircularProgress />
				</Box>
			</PageFrame>
		)
	}

	if (notFound) {
		return (
			<PageFrame>
				<Typography variant="h6" component="h1" mb={1}>
					Message not found
				</Typography>
				<Typography variant="body2" color="textSecondary">
					This message is not in the unidentified inbox. It may have been identified already, or the link may
					point at another region.
				</Typography>
			</PageFrame>
		)
	}

	if (error || !data) {
		return (
			<PageFrame>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography color="error">{error || "Failed to load the message"}</Typography>
					<Button size="small" onClick={refetch}>
						Retry
					</Button>
				</Box>
			</PageFrame>
		)
	}

	if (!data.UnidentifiedInboxEnabled) {
		return (
			<PageFrame>
				<Alert severity="info">The unidentified inbox is not available for your account yet.</Alert>
			</PageFrame>
		)
	}

	const {Message: message, Thread: thread, Candidates: candidates} = data
	const presenter = channelPresenter(message.Channel)
	const isOpen = message.Status === "open"

	return (
		<PageFrame>
			<ItemHeader message={message} presenter={presenter} />
			<Box maxWidth={960}>
				<Thread messages={thread} threadSubject={message.Subject} senderAddress={message.From.Address} />
				<Box mt={4}>
					<IdentifyPanel
						candidates={
							isOpen ? (
								<CandidateList
									candidates={candidates}
									renderActions={(candidate) => (
										<AssignButton
											name={candidate.Name}
											onAssign={() => assignTo(candidate.CustomerID, candidate.Name)}
											disabled={assigning}
										/>
									)}
								/>
							) : undefined
						}
						outcome={
							!isOpen && message.Resolution ? <OutcomeCard resolution={message.Resolution} /> : undefined
						}
						actions={
							isOpen ? (
								<>
									{message.From.Address ? (
										<SenderCheckbox
											address={message.From.Address}
											checked={addSenderEmail}
											onChange={setAddSenderEmail}
											disabled={assigning}
										/>
									) : null}
									<DismissPanel
										channel={message.Channel}
										conversationID={message.ConversationID}
										providerMessageID={message.ProviderMessageID}
										agentEmail={agentEmail}
										onDismissed={refetch}
									/>
								</>
							) : undefined
						}
					/>
				</Box>
			</Box>
		</PageFrame>
	)
}

export {ItemPage}
